import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/i18n/LangContext";
import BottomNav from "@/components/BottomNav";
import AdminSidebar from "@/components/AdminSidebar";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import JoinCompany from "@/pages/JoinCompany";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import { PlanSuccess, PlanCancel } from "@/pages/PlanCheckoutResult";
import Inicio from "@/pages/Inicio";
import Trabajos from "@/pages/Trabajos";
import Embarcaciones from "@/pages/Embarcaciones";
import Perfil from "@/pages/Perfil";
import DetalleTrabajo from "@/pages/DetalleTrabajo";
import MantenimientoForm from "@/pages/MantenimientoForm";
import MisHoras from "@/pages/MisHoras";
import Calendario from "@/pages/Calendario";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Plan from "@/pages/Plan";
import Pro from "@/pages/Pro";
import {
  GestionClientes, GestionServicios, GestionUsuarios,
  GestionEmbarcaciones, GestionAlbaranes, GestionAjustes
} from "@/pages/Gestion";
import ExtenderPrueba from "@/pages/ExtenderPrueba";
import GestionLeads from "@/pages/GestionLeads";
import SuperAdminTenants from "@/pages/SuperAdminTenants";
import { TrialGate, savePendingPay } from "@/components/TrialExpiredWall";
import "@/App.css";

const Protected = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-slate-200 border-t-[var(--coral)] rounded-full animate-spin" /></div>;
  if (!user) { savePendingPay(window.location.search); return <Navigate to="/login" replace />; }
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

const usePushNavigation = () => {
  const nav = useNavigate();
  useEffect(() => {
    const onNav = (e) => {
      const link = e.detail || "/";
      try { nav(link.startsWith("http") ? new URL(link).pathname : link); } catch { nav("/"); }
    };
    window.addEventListener("sns:navigate", onNav);
    return () => window.removeEventListener("sns:navigate", onNav);
  }, [nav]);
};

function Shell() {
  const { user } = useAuth();
  usePushNavigation();
  const isAdmin = user?.role === "admin";

  const routes = (
    <Routes>
      <Route path="/" element={<Inicio />} />
      <Route path="/trabajos" element={<Trabajos />} />
      <Route path="/trabajos/:id" element={<DetalleTrabajo />} />
      <Route path="/embarcaciones" element={<Embarcaciones />} />
      <Route path="/perfil" element={<Perfil />} />
      <Route path="/plan" element={<Plan />} />
      <Route path="/mis-horas" element={<MisHoras />} />
      <Route path="/calendario" element={<Protected adminOnly><Calendario /></Protected>} />
      <Route path="/nuevo-mantenimiento" element={<Protected adminOnly><MantenimientoForm /></Protected>} />
      <Route path="/editar-mantenimiento/:id" element={<Protected adminOnly><MantenimientoForm /></Protected>} />
      <Route path="/gestion/clientes" element={<Protected adminOnly><GestionClientes /></Protected>} />
      <Route path="/gestion/servicios" element={<Protected adminOnly><GestionServicios /></Protected>} />
      <Route path="/gestion/usuarios" element={<Protected adminOnly><GestionUsuarios /></Protected>} />
      <Route path="/gestion/embarcaciones" element={<Protected adminOnly><GestionEmbarcaciones /></Protected>} />
      <Route path="/gestion/albaranes" element={<Protected adminOnly><GestionAlbaranes /></Protected>} />
      <Route path="/super-admin/tenants" element={<Protected adminOnly><SuperAdminTenants /></Protected>} />
      <Route path="/gestion/leads" element={user?.is_super_admin ? <GestionLeads /> : <Navigate to="/" replace />} />
      <Route path="/gestion/ajustes" element={<Protected adminOnly><GestionAjustes /></Protected>} />
      {/* Compat: ruta antigua /gestion/informes redirige a albaranes */}
      <Route path="/gestion/informes" element={<Navigate to="/gestion/albaranes" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  return <TrialGate>{isAdmin ? (
      <div className="admin-shell">
        <AdminSidebar />
        <div className="mobile-shell lg:max-w-none lg:px-0 lg:pb-0">
          {routes}
        </div>
        <BottomNav />
      </div>
    ) : (
    <div className="mobile-shell">
      {routes}
      <BottomNav />
    </div>
  )}</TrialGate>;
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <Toaster richColors position="top-center" closeButton />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/unirse" element={<JoinCompany />} />
            <Route path="/join" element={<JoinCompany />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/plan/success" element={<Protected><PlanSuccess /></Protected>} />
            <Route path="/extender-prueba" element={<ExtenderPrueba />} />
            <Route path="/plan/cancel" element={<Protected><PlanCancel /></Protected>} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacidad" element={<PrivacyPolicy />} />
            <Route path="/pro" element={<Pro />} />
            <Route path="/precios" element={<Pro />} />
            <Route path="/pricing" element={<Pro />} />
            <Route path="/*" element={<Protected><Shell /></Protected>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
